<template>
  <Dialog
    v-model:visible="visibleModel"
    :style="{ width: '500px' }"
    :header="shift && shift.readOnly ? 'Shift Details' : 'Edit Shift'"
    :modal="true"
  >
    <div class="shift-form" v-if="shift">
      <!-- Read-only view -->
      <div v-if="shift.readOnly" class="shift-details">
        <div class="read-only-indicator">
          <i class="pi pi-lock"></i>
          <span>Read-only view</span>
        </div>
        <div class="detail-row">
          <div class="detail-label">Staff Member:</div>
          <div class="detail-value">{{ getStaffName(shift.userId) }}</div>
        </div>
        <div class="detail-row">
          <div class="detail-label">Time:</div>
          <div class="detail-value">
            {{ formatTime(shift.startTime) }} - {{ formatTime(shift.endTime) }}
          </div>
        </div>
        <div class="detail-row" v-if="shift.notes">
          <div class="detail-label">Notes:</div>
          <div class="detail-value">{{ shift.notes }}</div>
        </div>
      </div>

      <!-- Editable form (only for admins) -->
      <div v-else>
        <div class="form-group">
          <label><span class="required">*</span> Staff Member</label>
          <AutoComplete
            ref="staffInputRef"
            v-model="selectedStaff"
            :suggestions="filteredStaff"
            optionLabel="name"
            placeholder="Type or select staff..."
            @complete="searchStaff"
            @keydown.enter="handleStaffEnter"
            @keydown.tab="handleStaffTab"
            @change="checkForConflicts"
            :forceSelection="true"
            :completeOnFocus="true"
            :dropdown="true"
            :autoOptionFocus="false"
          />
        </div>
        <div class="form-group">
          <label><span class="required">*</span> Start Time</label>
          <TimeInput
            ref="startTimeRef"
            v-model="localShift.startTime"
            :referenceDate="localShift.date"
            @range-detected="handleEndTimeDetected"
            @enter-pressed="handleEnterPressed"
            @blur="checkForConflicts"
          />
        </div>
        <div class="form-group">
          <label><span class="required">*</span> End Time</label>
          <TimeInput
            ref="endTimeRef"
            v-model="localShift.endTime"
            :referenceDate="localShift.date"
            @enter-pressed="handleEnterPressed"
            @blur="checkForConflicts"
          />
        </div>
        <div class="form-group">
          <label>Notes</label>
          <InputText
            ref="notesRef"
            v-model="localShift.notes"
            @keydown.enter="onSave"
          />
        </div>

        <!-- Conflicts Warning Display -->
        <div v-if="conflicts.length > 0" class="conflict-warnings">
          <div class="conflict-header">
            <i class="pi pi-exclamation-triangle" style="color: #f59e0b"></i>
            <span>{{
              conflicts.length > 1
                ? "Scheduling Conflicts Detected"
                : "Scheduling Conflict Detected"
            }}</span>
          </div>
          <div class="conflict-list">
            <div
              v-for="(conflict, index) in conflicts"
              :key="index"
              class="conflict-item"
            >
              <div class="conflict-time" v-if="conflict.time_overlap">
                <b>
                  {{ formatTime(conflict.time_overlap.start) }} -
                  {{ formatTime(conflict.time_overlap.end) }}
                </b>
              </div>

              <div class="conflict-details">
                <template v-if="conflict.type === 'shift'">
                  at {{ conflict.store_name || "Unknown Store" }}
                </template>
                <template v-else-if="conflict.type === 'timeoff'">
                  Unavailable due to time off! ({{
                    conflict.reason || "No reason provided"
                  }})
                </template>
              </div>
            </div>
          </div>
          <div class="conflict-actions">
            <Button
              label="Save Anyway"
              icon="pi pi-check"
              class="p-button-warning"
              @click="onSave(true)"
              v-if="conflicts.length > 0"
              :disabled="isSaving"
            />
          </div>
        </div>

        <!-- Duplicates Warning Display -->
        <div v-if="duplicate" class="duplicate-warning">
          <div class="conflict-header">
            <i class="pi pi-clone" style="color: #8b5cf6"></i>
            <span>Duplicate Shift Detected</span>
          </div>
          <div class="conflict-details">
            This appears to be a duplicate of an existing shift for this staff
            member.
          </div>
          <div class="conflict-actions">
            <Button
              label="Save Anyway"
              icon="pi pi-check"
              class="p-button-warning"
              @click="onSave(true)"
              :disabled="isSaving"
            />
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <Button
        label="Close"
        icon="pi pi-times"
        @click="onCancel"
        class="p-button-text"
        :disabled="isSaving"
      />
      <Button
        v-if="shift && shift.id && !shift.readOnly"
        label="Delete"
        icon="pi pi-trash"
        class="p-button-danger p-button-text"
        @click="$emit('delete')"
        :disabled="isSaving"
      />
      <Button
        v-if="!shift?.readOnly"
        label="Save"
        icon="pi pi-check"
        @click="onSave()"
        :disabled="conflicts.length > 0 || duplicate || isSaving"
        :loading="isSaving"
      />
    </template>
  </Dialog>
</template>

<script>
import { computed, nextTick, ref, watch } from "vue";
import { formatTime } from "@/lib/timeUtils";
import { useStaffAutocomplete } from "../../composables/useStaffAutocomplete";
import TimeInput from "../../components/TimeInput.vue";

export default {
  name: "ShiftDialog",

  components: {
    TimeInput,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    shift: {
      type: Object,
      default: null,
    },
    staffMembers: {
      type: Array,
      default: () => [],
    },
    shiftsService: {
      type: Object,
      required: true,
    },
    existingShifts: {
      type: Array,
      default: () => [],
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["update:visible", "save", "delete", "cancel", "validation-error"],

  setup(props, { emit }) {
    const localShift = ref(null);
    const conflicts = ref([]);
    const duplicate = ref(false);
    const checkingInProgress = ref(false);

    // Template refs for focus management
    const staffInputRef = ref(null);
    const startTimeRef = ref(null);
    const endTimeRef = ref(null);
    const notesRef = ref(null);

    // Function to get staff name by ID
    const getStaffName = (userId) => {
      const staff = props.staffMembers.find((s) => s.id === userId);
      return staff ? staff.name : "Unknown";
    };

    // Use the staff autocomplete composable
    const staffMembersRef = computed(() => props.staffMembers || []);
    const userIdRef = computed({
      get: () => localShift.value?.userId,
      set: (val) => {
        if (localShift.value) {
          localShift.value.userId = val;
        }
      },
    });

    const {
      selectedStaff,
      filteredStaff,
      searchStaff,
      handleEnter: staffHandleEnter,
      handleTab: staffHandleTab,
      initFromUserId,
      reset: resetStaffAutocomplete,
    } = useStaffAutocomplete({
      staffMembers: staffMembersRef,
      onSelected: () => focusStartTime(),
      userIdRef,
    });

    // Focus helpers using refs
    const focusStartTime = () => {
      nextTick(() => {
        const input = startTimeRef.value?.$el?.querySelector("input") ||
                      startTimeRef.value?.$el;
        input?.focus();
        input?.select?.();
      });
    };

    const focusNotes = () => {
      nextTick(() => {
        notesRef.value?.$el?.focus();
      });
    };

    // Handle Enter key in staff AutoComplete
    const handleStaffEnter = (event) => {
      const shouldSave = staffHandleEnter(event, { advanceOnly: false });
      if (shouldSave) {
        onSave();
      }
    };

    // Handle Tab key in staff AutoComplete
    const handleStaffTab = (event) => {
      staffHandleTab(event);
    };

    // Two-way binding for visible property
    const visibleModel = computed({
      get: () => props.visible,
      set: (value) => emit("update:visible", value),
    });


    // Focus first field when dialog becomes visible
    // Use watcher + setTimeout to ensure DOM is ready and focus trap is initialized
    watch(
      () => props.visible,
      (isVisible) => {
        if (isVisible && !props.shift?.readOnly) {
          setTimeout(() => {
            const input = staffInputRef.value?.$el?.querySelector("input");
            input?.focus();
          }, 300);
        }
      }
    );


    // Update local shift data when prop changes
    watch(
      () => props.shift,
      (newShift) => {
        if (newShift) {
          const shiftCopy = { ...newShift };

          if (!shiftCopy.id) {
            if (shiftCopy.startTime) {
              shiftCopy.startTime = undefined;
              shiftCopy.endTime = undefined;
            }
          }

          localShift.value = shiftCopy;

          // Initialize staff selection
          initFromUserId(shiftCopy.userId, props.staffMembers);

          // Reset conflicts when shift changes
          conflicts.value = [];
          duplicate.value = false;
        }
      },
      { immediate: true }
    );

    // Watch for changes in shift times or staff to check for conflicts
    watch(
      () => [
        localShift.value?.userId,
        localShift.value?.startTime,
        localShift.value?.endTime,
      ],
      () => {
        if (
          localShift.value &&
          !checkingInProgress.value &&
          !props.shift?.readOnly
        ) {
          setTimeout(() => checkForConflicts(), 500);
        }
      },
      { deep: true }
    );

    // Handle when a time range is detected in the start time field
    const handleEndTimeDetected = (endTime) => {
      if (endTime && localShift.value) {
        localShift.value.endTime = endTime;
        focusNotes();
      }
    };

    // Handle when Enter is pressed in any TimeInput
    const handleEnterPressed = () => {
      onSave();
    };

    // Check for conflicts
    const checkForConflicts = async () => {
      if (props.shift?.readOnly) {
        conflicts.value = [];
        duplicate.value = false;
        return;
      }

      if (
        !localShift.value ||
        !localShift.value.userId ||
        !localShift.value.startTime ||
        !localShift.value.endTime
      ) {
        conflicts.value = [];
        duplicate.value = false;
        return;
      }

      checkingInProgress.value = true;
      try {
        if (props.shiftsService && props.shiftsService.isDuplicateShift) {
          duplicate.value = !!props.shiftsService.isDuplicateShift(
            {
              ...localShift.value,
              id: props.shift.id,
            },
            props.existingShifts
          );
        }

        if (props.shiftsService && props.shiftsService.checkShiftConflicts) {
          const result = await props.shiftsService.checkShiftConflicts(
            localShift.value,
            props.shift.id
          );

          if (result && result.conflicts) {
            conflicts.value = result.conflicts;
          } else {
            conflicts.value = [];
          }
        }
      } catch (err) {
        console.error("Error checking conflicts:", err);
        conflicts.value = [];
      } finally {
        checkingInProgress.value = false;
      }
    };

    // Handle save action
    const onSave = async (forceOverride = false) => {
      if (!localShift.value || !localShift.value.userId) {
        emit("validation-error", "Please select a staff member");
        return;
      }

      if (!localShift.value.startTime || !localShift.value.endTime) {
        emit("validation-error", "Please enter valid start and end times");
        return;
      }

      if (localShift.value.endTime <= localShift.value.startTime) {
        emit("validation-error", "End time must be after start time");
        return;
      }

      if (
        !forceOverride &&
        localShift.value.userId &&
        localShift.value.startTime &&
        localShift.value.endTime
      ) {
        await checkForConflicts();

        if ((conflicts.value.length > 0 || duplicate.value) && !forceOverride) {
          return;
        }
      }

      const options = {
        skipConflictCheck: forceOverride,
        skipDuplicateCheck: forceOverride,
      };

      emit("save", localShift.value, options);
    };

    // Handle cancel action
    const onCancel = () => {
      emit("cancel");
    };

    return {
      localShift,
      visibleModel,
      conflicts,
      duplicate,
      selectedStaff,
      filteredStaff,
      searchStaff,
      handleStaffEnter,
      handleStaffTab,
      onSave,
      onCancel,
      handleEndTimeDetected,
      handleEnterPressed,
      checkForConflicts,
      formatTime,
      getStaffName,
      // Template refs
      staffInputRef,
      startTimeRef,
      endTimeRef,
      notesRef,
    };
  },
};
</script>

<style scoped>
.shift-details {
  padding: 1rem 0;
}
.read-only-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #f0f8ff;
  padding: 0.5rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: #0077cc;
}
.detail-row {
  display: flex;
  margin-bottom: 0.75rem;
}
.detail-label {
  font-weight: 600;
  width: 120px;
}
.detail-value {
  flex: 1;
}
.shift-form {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-top: 1rem;
}
.form-group {
  display: flex;
  flex-direction: column;
}
.form-group label {
  font-weight: 600;
  margin-bottom: 0.25rem;
}
.required {
  color: #ef4444;
  margin-right: 0.25rem;
}

/* Conflict warning styles */
.conflict-warnings,
.duplicate-warning {
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 8px;
  background-color: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.3);
}
.duplicate-warning {
  background-color: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.3);
}
.conflict-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}
.conflict-list {
  margin-bottom: 1rem;
}
.conflict-item {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}
.conflict-time {
  margin-bottom: 0.25rem;
}
.conflict-details {
  font-size: 0.9rem;
  color: #555;
}
.conflict-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.5rem;
}
</style>
